// components/UserStats.tsx
import React, { useMemo } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  useTheme,
  alpha,
} from '@mui/material';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  DateRange as DateRangeIcon,
  Today as TodayIcon,
  Timeline as TimelineIcon,
  ShowChart as ShowChartIcon
} from '@mui/icons-material';
import { User } from '../types/user';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, ResponsiveContainer
} from 'recharts';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

interface UserStatsProps {
  users: User[];
}

interface DateRangeStats {
  total: number;
  percentage: number;
  trend: 'up' | 'down' | 'stable';
}

const UserStats: React.FC<UserStatsProps> = ({ users }) => {
  const theme = useTheme();

  // Calculate all stats using useMemo for performance
  const stats = useMemo(() => {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - thisWeek.getDay());
    
    const lastWeek = new Date(thisWeek);
    lastWeek.setDate(lastWeek.getDate() - 7);
    
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    
    const last30Days = new Date(today);
    last30Days.setDate(last30Days.getDate() - 30);
    
    const thisYear = new Date(now.getFullYear(), 0, 1);
    const lastYear = new Date(now.getFullYear() - 1, 0, 1);

    // Helper to count users created after a date
    const countSince = (date: Date) => users.filter(u => {
      if (!u.created_at) return false;
      try {
        return new Date(u.created_at) >= date;
      } catch {
        return false;
      }
    }).length;

    // Helper to count users created between dates
    const countBetween = (start: Date, end: Date) => users.filter(u => {
      if (!u.created_at) return false;
      try {
        const created = new Date(u.created_at);
        return created >= start && created < end;
      } catch {
        return false;
      }
    }).length;

    const totalUsers = users.length;
    
    // Daily signups for the last 7 days
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      
      return {
        date: date.toLocaleDateString('en-US', { weekday: 'short' }),
        fullDate: date,
        count: countBetween(date, nextDay)
      };
    }).reverse();

    // Monthly signups for the last 12 months
    const last12Months = Array.from({ length: 12 }, (_, i) => {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
      
      return {
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        fullDate: date,
        count: countBetween(date, nextMonth)
      };
    }).reverse();

    // Role distribution
    const roleDistribution = {
      customers: users.filter(u => u.role === 'customer').length,
      locationAdmins: users.filter(u => u.role === 'location_admin').length,
      superAdmins: users.filter(u => u.role === 'super_admin').length
    };

    // Calculate trends
    const getTrend = (current: number, previous: number): DateRangeStats => {
      if (previous === 0) return { total: current, percentage: current > 0 ? 100 : 0, trend: 'up' };
      const percentage = ((current - previous) / previous) * 100;
      return {
        total: current,
        percentage: Math.abs(Math.round(percentage * 10) / 10),
        trend: percentage > 0 ? 'up' : percentage < 0 ? 'down' : 'stable'
      };
    };

    // Calculate average weekly signups (last 30 days / 4.3)
    const last30DaysCount = countSince(last30Days);
    const avgWeekly = Math.round((last30DaysCount / 30) * 7 * 10) / 10;

    return {
      totals: {
        total: totalUsers,
        customers: roleDistribution.customers,
        admins: roleDistribution.locationAdmins + roleDistribution.superAdmins
      },
      dateRanges: {
        today: getTrend(
          countSince(today),
          countBetween(yesterday, today)
        ),
        thisWeek: getTrend(
          countSince(thisWeek),
          countBetween(lastWeek, thisWeek)
        ),
        thisMonth: getTrend(
          countSince(thisMonth),
          countBetween(lastMonth, thisMonth)
        ),
        last30Days: {
          total: last30DaysCount,
          percentage: 0,
          trend: 'stable' as const
        },
        thisYear: getTrend(
          countSince(thisYear),
          countBetween(lastYear, thisYear)
        ),
        avgWeekly: {
          total: avgWeekly,
          percentage: 0,
          trend: 'stable' as const
        }
      },
      charts: {
        daily: last7Days,
        monthly: last12Months,
        distribution: roleDistribution
      }
    };
  }, [users]);

  const StatCard = ({ 
    icon, 
    label, 
    value, 
    trend, 
    color = theme.palette.primary.main,
    tooltip
  }: { 
    icon: React.ReactNode; 
    label: string; 
    value: number | string;
    trend?: { percentage: number; trend: 'up' | 'down' | 'stable' };
    color?: string;
    tooltip?: string;
  }) => (
    <Paper
      sx={{
        p: 1.5,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        }
      }}
      title={tooltip}
    >
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {icon}
          {label}
        </Typography>
        <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {trend && trend.percentage > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            {trend.trend === 'up' && <TrendingUpIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />}
            {trend.trend === 'down' && <TrendingDownIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />}
            <Typography variant="caption" color={trend.trend === 'up' ? 'success.main' : trend.trend === 'down' ? 'error.main' : 'text.secondary'}>
              {trend.percentage}% {trend.trend === 'stable' ? '' : trend.trend}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          bgcolor: alpha(color, 0.1),
          borderRadius: 2,
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ color }}>{icon}</Box>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ mb: 3 }}>
      {/* KPI Row - 8 cards, 2 rows on mobile, 1 row on desktop */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard 
            icon={<PeopleIcon sx={{ fontSize: 20 }} />}
            label="Total Users"
            value={stats.totals.total}
            color={theme.palette.primary.main}
            tooltip="All registered users"
          />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard 
            icon={<PersonIcon sx={{ fontSize: 20 }} />}
            label="Customers"
            value={stats.totals.customers}
            color={theme.palette.success.main}
            tooltip="Users with customer role"
          />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard 
            icon={<AdminPanelSettingsIcon sx={{ fontSize: 20 }} />}
            label="Admins"
            value={stats.totals.admins}
            color={theme.palette.warning.main}
            tooltip="Super admins + location admins"
          />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard 
            icon={<TodayIcon sx={{ fontSize: 20 }} />}
            label="Today"
            value={stats.dateRanges.today.total}
            trend={stats.dateRanges.today}
            color={theme.palette.info.main}
            tooltip="New users today"
          />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard 
            icon={<DateRangeIcon sx={{ fontSize: 20 }} />}
            label="This Week"
            value={stats.dateRanges.thisWeek.total}
            trend={stats.dateRanges.thisWeek}
            color={theme.palette.secondary.main}
            tooltip="New users this week"
          />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard 
            icon={<DateRangeIcon sx={{ fontSize: 20 }} />}
            label="This Month"
            value={stats.dateRanges.thisMonth.total}
            trend={stats.dateRanges.thisMonth}
            color={theme.palette.error.main}
            tooltip="New users this month"
          />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard 
            icon={<TimelineIcon sx={{ fontSize: 20 }} />}
            label="Last 30 Days"
            value={stats.dateRanges.last30Days.total}
            color={theme.palette.warning.dark}
            tooltip="New users in the last 30 days"
          />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard 
            icon={<ShowChartIcon sx={{ fontSize: 20 }} />}
            label="Avg Weekly"
            value={stats.dateRanges.avgWeekly.total}
            color={theme.palette.success.dark}
            tooltip="Average weekly signups (last 30 days)"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={2}>
        {/* Daily Signups Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TodayIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
              Daily Signups (Last 7 Days)
            </Typography>
            <Box sx={{ height: 150 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.charts.daily}>
                  <defs>
                    <linearGradient id="dailyGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <RechartsTooltip />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke={theme.palette.primary.main}
                    fill="url(#dailyGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Monthly Signups Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DateRangeIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
              Monthly Signups (Last 12 Months)
            </Typography>
            <Box sx={{ height: 150 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.charts.monthly}>
                  <defs>
                    <linearGradient id="monthlyGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={2} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <RechartsTooltip />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke={theme.palette.success.main}
                    fill="url(#monthlyGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserStats;